import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOnlineStatus } from "./useOnlineStatus";
import { useFeatureFlag } from "./useFeatureFlag";
import { useAuth } from "./useAuth";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { getQueueStats, isIndexedDBAvailable } from "@/lib/offlineStore";
import { processSyncQueue, type SyncProgress, type SyncResult } from "@/lib/syncEngine";
import { fetchInspections, inspectionsKeys } from "./useInspectionsQuery";
import { defectsKeys, fetchDefects } from "./useDefectsQuery";
import { assignableProfilesKeys, fetchWithOfflineFallback } from "./useAssignableProfiles";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

/**
 * Hook that connects the sync engine with online status.
 * Automatically starts sync when: isOnline === true && queueLength > 0
 * Also performs a one-time background prefetch of defects and inspections
 * so they are available in IndexedDB before the user navigates to those pages.
 * Only active when 'offline-mode' feature flag is enabled.
 */
export function useSyncEngine() {
  const { t } = useTranslation();
  const { isOnline } = useOnlineStatus();
  const { enabled: offlineEnabled } = useFeatureFlag(
    "offline-mode",
    FEATURE_FLAGS["offline-mode"] ?? false,
  );
  const { user, isAdmin, isQualitaetsmanager } = useAuth();

  const queryClient = useQueryClient();
  const [pendingCount, setPendingCount] = useState(0);
  const [deadLetterCount, setDeadLetterCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [lastSyncError, setLastSyncError] = useState<string | null>(null);
  const syncingRef = useRef(false);
  const hasPrefetched = useRef(false);

  // Poll queue length periodically (single IDB read via getQueueStats)
  const refreshPendingCount = useCallback(async () => {
    if (!offlineEnabled || !isIndexedDBAvailable()) return;
    try {
      const { pendingCount: count, deadLetters } = await getQueueStats();
      setPendingCount(count);
      setDeadLetterCount(deadLetters.length);
    } catch {
      // IndexedDB not available
    }
  }, [offlineEnabled]);

  // Refresh pending count on mount and when online status changes
  useEffect(() => {
    refreshPendingCount();
    const interval = setInterval(refreshPendingCount, 5000);
    return () => clearInterval(interval);
  }, [refreshPendingCount]);

  // One-time background prefetch for offline availability
  // fallow-ignore-next-line complexity
  useEffect(() => {
    if (!isOnline || !offlineEnabled || !user || hasPrefetched.current || !isIndexedDBAvailable())
      return;
    hasPrefetched.current = true;

    const canSeeAll = isAdmin || isQualitaetsmanager;

    // Prefetch inspections into TanStack cache + IndexedDB (best-effort, silent errors)
    const prefetchInspections = async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey: inspectionsKeys.list({ userId: user.id, canSeeAll }),
          queryFn: () => fetchInspections(user.id, canSeeAll, true, true),
          staleTime: 5 * 60 * 1000,
        });
      } catch {
        // Best-effort prefetch – ignore errors
      }
    };

    // Prefetch defects into TanStack cache + IndexedDB
    // fetchDefects already writes to IndexedDB when offlineEnabled=true,
    // so we just need to trigger the query via prefetchQuery with a queryFn
    const prefetchDefects = async () => {
      try {
        // defectsKeys from this module is already statically imported above, so a
        // dynamic import of fetchDefects split nothing (INEFFECTIVE_DYNAMIC_IMPORT).
        await queryClient.prefetchQuery({
          queryKey: defectsKeys.list({ userId: user.id, canSeeAll }),
          queryFn: () => fetchDefects(user.id, canSeeAll, true, true),
          staleTime: 5 * 60 * 1000,
        });
      } catch {
        // Best-effort prefetch – ignore errors
      }
    };

    // Prefetch assignable profiles into TanStack cache + IndexedDB
    const prefetchProfiles = async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey: assignableProfilesKeys.defects,
          queryFn: () =>
            fetchWithOfflineFallback(
              "list_assignable_profiles",
              "assignable-profiles-defects",
              true,
              true,
            ),
          staleTime: 5 * 60 * 1000,
        });
        await queryClient.prefetchQuery({
          queryKey: assignableProfilesKeys.inspections,
          queryFn: () =>
            fetchWithOfflineFallback(
              "list_inspection_assignable_profiles",
              "assignable-profiles-inspections",
              true,
              true,
            ),
          staleTime: 5 * 60 * 1000,
        });
      } catch {
        // Best-effort prefetch – ignore errors
      }
    };

    // Run all prefetches in parallel, don't block UI
    Promise.all([prefetchInspections(), prefetchDefects(), prefetchProfiles()]).catch(() => {});
  }, [isOnline, offlineEnabled, user, isAdmin, isQualitaetsmanager, queryClient]);

  // Auto-sync when coming online with pending items
  // fallow-ignore-next-line complexity
  useEffect(() => {
    if (!offlineEnabled || !isOnline || pendingCount === 0 || syncingRef.current) return;

    // fallow-ignore-next-line complexity
    const runSync = async () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      setIsSyncing(true);
      setLastSyncError(null);

      try {
        const result: SyncResult = await processSyncQueue({
          onProgress: (progress) => setSyncProgress(progress),
          onError: (error) => setLastSyncError(error),
        });

        // Invalidate all relevant caches after sync so UI reflects synced data
        if (result.synced > 0) {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["defects"] }),
            queryClient.invalidateQueries({ queryKey: ["item-defects"] }),
            queryClient.invalidateQueries({ queryKey: ["inspections"] }),
            queryClient.invalidateQueries({ queryKey: ["inspection-responses"] }),
            queryClient.invalidateQueries({ queryKey: ["open-counts"] }),
          ]);
        }

        if (result.synced > 0 && result.failed === 0) {
          toast.success(t("common.syncComplete"));
        } else if (result.failed > 0) {
          toast.error(t("common.syncFailed"));
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setLastSyncError(msg);
        toast.error(t("common.syncFailed"));
      } finally {
        setIsSyncing(false);
        setSyncProgress(null);
        syncingRef.current = false;
        await refreshPendingCount();
      }
    };

    // Small delay to let connection stabilize
    const timeout = setTimeout(runSync, 2000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryClient is stable singleton; deadLetterCount excluded intentionally (polling via getQueueStats)
  }, [isOnline, pendingCount, offlineEnabled, t, refreshPendingCount]);

  return {
    pendingCount,
    deadLetterCount,
    isSyncing,
    syncProgress,
    lastSyncError,
    refreshPendingCount,
    isOfflineCapable: offlineEnabled && isIndexedDBAvailable(),
  };
}
